import {
    computed,
    type ComputedRef,
    ref,
    type Ref,
    type WritableComputedRef,
} from 'vue'
import { onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router'
import type {
    QuestionnaireQuestion,
    QuestionnaireStepContentContentFragment,
    QuestionnaireStepContentQuestionsFragment,
    QuestionnaireStepFragment,
} from '@/graphql/api'
import { evaluateConditions } from './question/composables/evaluateConditions.ts'
import { getAnswer, useQuestionnaireStore } from './useQuestionnaireStore'
import { useActivated } from '@/composables/useActivated'
import { useRedirect } from 'wucdbm-vite-vue-ssr'
import { useRoutes } from '@/router/routes'
import { useObjectComputed } from '@/composables/useObjectComputed.ts'

type StepContentType =
    | QuestionnaireStepContentQuestionsFragment
    | QuestionnaireStepContentContentFragment

const questionPredicate = (
    content: StepContentType
): content is QuestionnaireStepContentQuestionsFragment =>
    content.__typename === 'QuestionnaireStepContentQuestions'

interface QuestionnaireNavigation {
    requiredQuestions: ComputedRef<QuestionnaireQuestion[]>
    stepsConditional: ComputedRef<QuestionnaireStepFragment[]>
    currentStepIndex: WritableComputedRef<number | 'complete'>
    currentStep: ComputedRef<QuestionnaireStepFragment | null | undefined>
    currentStepContentConditional: ComputedRef<
        QuestionnaireStepFragment['content'] | undefined
    >
    isComplete: ComputedRef<boolean>
    isOnLastStep: ComputedRef<boolean>
    isNextDisabled: ComputedRef<boolean>
    progressIndicatorStepCount: ComputedRef<number>
    progressIndicatorStepCurrent: ComputedRef<number>
    incrementStep(): void
    goBack(): void
    isOnLoginBeforeStartingScreen: ComputedRef<boolean>
}

export default function useQuestionnaireNavigation(
    steps: Readonly<Ref<QuestionnaireStepFragment[]>>,
    questionnaireId: Readonly<Ref<string>>,
    isSubmitted: Readonly<Ref<boolean>>,
    requireLoginBeforeStarting: Readonly<Ref<boolean | undefined>>,
    isLogged: Readonly<Ref<boolean>>
): QuestionnaireNavigation {
    const route = useRoute()
    const router = useRouter()
    const redirect = useRedirect()
    const routes = useRoutes()
    const step = route.params['step']

    // Start from beginning on landing
    if (step === 'complete' || Number(step || 0)) {
        const id = route.params['id']

        if (typeof id === 'string') {
            redirect(
                router.resolve(
                    routes.questionnaire({ __typename: 'Questionnaire', id })
                ).href
            )
        }
    }

    // Prevents the questionnaire switching to step 0 when the
    // user navigates to another route (caused by KeepAlive)
    const currentStepIndexCached = ref<number | 'complete'>(0)
    const currentStepIndex = computed({
        get() {
            if (isSubmitted.value) {
                return currentStepIndexCached.value
            }

            const step = route.params['step']

            return (currentStepIndexCached.value =
                step === 'complete' ? step : Number(step || 0))
        },
        set(step: number | 'complete') {
            const r = {
                ...route,
                params: {
                    ...route.params,
                    step: String(step),
                },
            }

            router.push(r)
        },
    })

    const isComplete = computed(() => currentStepIndex.value === 'complete')

    const questionnaireStore = useQuestionnaireStore()

    const stepsConditional: ComputedRef<QuestionnaireStepFragment[]> = computed(
        () =>
            steps.value.filter((step) =>
                evaluateConditions(
                    step.conditions,
                    questionnaireStore,
                    questionnaireId.value
                )
            )
    )

    const currentStep: ComputedRef<
        QuestionnaireStepFragment | null | undefined
    > = useObjectComputed(() => {
        if (currentStepIndex.value === 'complete') {
            return null
        }

        return stepsConditional.value[currentStepIndex.value]
    })

    const currentStepContentConditional: ComputedRef<
        QuestionnaireStepFragment['content'] | undefined
    > = computed(() =>
        currentStep.value?.content.filter((step) =>
            evaluateConditions(
                step.conditions,
                questionnaireStore,
                questionnaireId.value
            )
        )
    )

    const requiredQuestions: ComputedRef<QuestionnaireQuestion[]> = computed(
        () =>
            currentStepIndex.value === 'complete'
                ? []
                : currentStepContentConditional.value
                      ?.filter(questionPredicate)
                      ?.flatMap((question) => question.questions)
                      .filter((question) => question.required) || []
    )

    const isNextDisabled = computed(() => {
        return requiredQuestions.value.some((question) => {
            const answer =
                questionnaireStore.answers[questionnaireId.value]?.[question.id]
                    ?.answer

            if (!answer || !answer.answer) {
                return true
            }

            const theAnswer = getAnswer(answer)

            return Array.isArray(theAnswer)
                ? !theAnswer.length
                : theAnswer === 0
                  ? false
                  : !theAnswer
        })
    })

    // prevent forward navigation if required questions are not answered
    // happens when the user goes back to a required question, clears it and
    // presses the browser's forward button
    const { isActivated } = useActivated()
    onBeforeRouteUpdate((to, from) => {
        if (!isActivated.value) {
            return
        }

        return !(
            to.params['step'] &&
            from.params['step'] &&
            to.params['step'] > from.params['step'] &&
            isNextDisabled.value
        )
    })

    const isOnLastStep = computed(
        () => currentStepIndex.value === stepsConditional.value.length - 1
    )

    const incrementStep = () => {
        if (isNextDisabled.value || currentStepIndex.value === 'complete') {
            return
        }

        questionnaireStore.setStep(
            currentStepIndex.value,
            questionnaireId.value
        )

        if (isOnLastStep.value) {
            currentStepIndex.value = 'complete'
        } else {
            currentStepIndex.value += 1
        }
    }

    const goBack = () => {
        if (currentStepIndex.value === 'complete') {
            currentStepIndex.value = stepsConditional.value.length - 1
        } else {
            currentStepIndex.value -= 1
        }
    }

    const progressIndicatorStepCount = computed(
        () =>
            stepsConditional.value.filter(
                (step) => !step.excludeFromProgressIndicator
            ).length + 1 // add 1 for step 'complete'
    )

    const isOnLoginBeforeStartingScreen = computed(
        () => !isLogged.value && Boolean(requireLoginBeforeStarting.value)
    )

    const progressIndicatorStepCurrent = computed(() =>
        isOnLoginBeforeStartingScreen.value
            ? -1
            : stepsConditional.value
                  .slice(
                      0,
                      currentStepIndex.value === 'complete'
                          ? stepsConditional.value.length
                          : currentStepIndex.value + 1
                  )
                  .filter((step) => !step.excludeFromProgressIndicator).length -
              (currentStepIndex.value === 'complete' ? 0 : 1)
    )

    return {
        requiredQuestions,
        stepsConditional,
        currentStepIndex,
        currentStep,
        currentStepContentConditional,
        isComplete,
        isOnLastStep,
        isNextDisabled,
        progressIndicatorStepCount,
        progressIndicatorStepCurrent,
        incrementStep,
        goBack,
        isOnLoginBeforeStartingScreen,
    }
}
